import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Alert,
  Divider,
} from '@mui/material';
import { Hub, School, People, Subscriptions } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { brandColors } from '../../theme';
import schoolService from '../../services/schoolService';
import useAccess from '../../hooks/useAccess';
import { ChannelStats } from '../../types';

/** Bucket every school starts in until a real channel is assigned. */
const DEFAULT_CHANNEL = 'edulabs';

/** Channels are stored lower case; show them capitalised. */
const formatChannel = (channel: string) => channel.charAt(0).toUpperCase() + channel.slice(1);

const Metric: React.FC<{ icon: React.ReactNode; label: string; value: number }> = ({ icon, label, value }) => (
  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', color: 'text.secondary', mb: 0.5 }}>{icon}</Box>
    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const ChannelsPage: React.FC = () => {
  const navigate = useNavigate();
  const access = useAccess();
  const { selected: selectedYear, current: currentYear } = useSelector((state: RootState) => state.academicYear);

  const [stats, setStats] = useState<ChannelStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A channel-confined user has only their own channel, so the overview says nothing.
  useEffect(() => {
    if (!access.canSeeChannels) {
      navigate('/schools', { replace: true });
    }
  }, [access.canSeeChannels, navigate]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    schoolService
      .getChannelStats(selectedYear)
      .then((res) => {
        if (!cancelled) {
          setStats(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load channels');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  const totalSchools = stats.reduce((sum, s) => sum + s.schools, 0);
  const unassigned = stats.find((s) => s.channel === DEFAULT_CHANNEL)?.schools ?? 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Channels
        </Typography>
        <Typography variant="body1" color="text.secondary">
          How schools and students are distributed across sales channels
        </Typography>
        {selectedYear != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`${selectedYear}${selectedYear === currentYear ? ' · Current' : ' · Previous'}`}
              color={selectedYear === currentYear ? 'primary' : 'default'}
            />
            <Typography variant="body2" color="text.secondary">
              {stats.length} channels · {totalSchools} {totalSchools === 1 ? 'school' : 'schools'}
            </Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!isLoading && unassigned > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {unassigned} {unassigned === 1 ? 'school is' : 'schools are'} still on the default{' '}
          <strong>{formatChannel(DEFAULT_CHANNEL)}</strong> channel. Assign the real channel from the school form.
        </Alert>
      )}

      {isLoading ? (
        <Typography>Loading channels...</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3 }}>
          {stats.map((channel) => {
            const isDefault = channel.channel === DEFAULT_CHANNEL;
            return (
              <Card
                key={channel.channel}
                onClick={() => navigate(`/schools?channel=${encodeURIComponent(channel.channel)}`)}
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        backgroundColor: isDefault ? 'grey.400' : brandColors.accent,
                        mr: 2,
                        width: 48,
                        height: 48,
                      }}
                    >
                      <Hub />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatChannel(channel.channel)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isDefault ? 'Default channel' : 'Assigned channel'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Metric icon={<School sx={{ fontSize: 18 }} />} label="Schools" value={channel.schools} />
                    <Metric icon={<People sx={{ fontSize: 18 }} />} label="Students" value={channel.students} />
                    <Metric
                      icon={<Subscriptions sx={{ fontSize: 18 }} />}
                      label="Subscriptions"
                      value={channel.subscriptions}
                    />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ChannelsPage;
